import type { Logger } from "pino";
import type { AgentManager, ManagedAgent } from "./agent-manager.js";
import type { AgentStorage } from "./agent-storage.js";
import type { AgentProvider } from "./agent-sdk-types.js";
import { getProviderFullAccessModeId } from "./provider-manifest.js";
import { scheduleAgentMetadataGeneration } from "./agent-metadata-generator.js";
import { escapeAppostleSystemAttr, setupFinishNotification, startAgentRun } from "./mcp-shared.js";

const CLAUDE_TIER_ALIASES: Record<string, { model: string; clearThinking: boolean }> = {
  haiku: { model: "claude-haiku-4-5", clearThinking: true },
  sonnet: { model: "claude-sonnet-4-6", clearThinking: false },
  opus: { model: "claude-opus-4-7", clearThinking: false },
};

export type HandoffSpawnedCallback = (params: {
  parentAgentId: string;
  parentAgentCwd: string;
  parentAgentOwnerUserId: string;
  childAgentId: string;
  childAgentTitle: string | null;
}) => Promise<void> | void;

export function resolveHandoffModel(
  requestedModel: string | undefined,
  targetProvider: AgentProvider,
  parentModel: string | undefined,
  parentThinkingOptionId: string | undefined,
): { model: string | undefined; thinkingOptionId: string | undefined } {
  if (!requestedModel) {
    return { model: parentModel, thinkingOptionId: parentThinkingOptionId };
  }
  if (targetProvider === "claude") {
    const alias = CLAUDE_TIER_ALIASES[requestedModel.trim().toLowerCase()];
    if (alias) {
      return {
        model: alias.model,
        thinkingOptionId: alias.clearThinking ? undefined : parentThinkingOptionId,
      };
    }
  }
  return { model: requestedModel, thinkingOptionId: parentThinkingOptionId };
}

export function buildHandoffBriefEnvelope(params: {
  shepherdId: string;
  shepherdTitle: string;
  taskBrief: string;
}): string {
  const { shepherdId, shepherdTitle, taskBrief } = params;
  const contract = [
    "## How to finish this session",
    "",
    "Your final action in this session MUST be a call to the `report` tool",
    "(`mcp__appostle__report`). The parent agent that handed off to you",
    "cannot see this conversation — it only sees the `message` you pass to",
    "`report`.",
    "",
    "- Lead with a one-line verdict, then fill in detail.",
    "- Make the report self-contained — include file paths, decisions, and",
    "  any blockers. The parent has no access to your timeline.",
    "- Do NOT end the session without calling `report`, even if the answer",
    "  is short, negative, or you got blocked early. A short report is fine;",
    "  silence is not.",
    "- You may call `report` multiple times for interim updates; the final",
    "  call should be the wrap-up.",
    "",
    "## Brief from your shepherd",
    "",
  ].join("\n");
  return [
    `<appostle-system kind="handoff-brief" agent-id="${escapeAppostleSystemAttr(shepherdId)}" title="${escapeAppostleSystemAttr(shepherdTitle)}">`,
    contract,
    "<display>",
    taskBrief,
    "</display>",
    "</appostle-system>",
  ].join("\n");
}

export async function spawnHandoffChild(params: {
  agentManager: AgentManager;
  agentStorage?: AgentStorage;
  appostleHome: string;
  logger: Logger;
  callerAgentId: string;
  task: string;
  title?: string;
  provider?: string;
  model?: string;
  onHandoffSpawned?: HandoffSpawnedCallback;
}): Promise<ManagedAgent> {
  const parentAgent = params.agentManager.getAgentSnapshot(params.callerAgentId);
  if (!parentAgent) {
    throw new Error(`Parent agent ${params.callerAgentId} not found`);
  }
  if (parentAgent.ownerUserId == null) {
    throw new Error(`handoff failed: parent agent ${params.callerAgentId} has no resolved owner`);
  }

  const targetProvider = params.provider ?? parentAgent.provider;
  const resolved = resolveHandoffModel(
    params.model,
    targetProvider,
    parentAgent.config.model,
    parentAgent.config.thinkingOptionId,
  );
  const targetModeId = getProviderFullAccessModeId(targetProvider) ?? undefined;
  const snapshot = await params.agentManager.spawnAgentFromParent(
    parentAgent,
    {
      provider: targetProvider,
      cwd: parentAgent.cwd,
      modeId: targetModeId,
      title: params.title?.trim() || undefined,
      model: resolved.model,
      thinkingOptionId: resolved.thinkingOptionId,
    },
    {
      labels: {
        "appostle.parent-agent-id": params.callerAgentId,
        "appostle.handoff": "true",
      },
    },
  );

  const taskBrief = params.task.trim();
  const prompt = buildHandoffBriefEnvelope({
    shepherdId: params.callerAgentId,
    shepherdTitle: parentAgent.title ?? parentAgent.config?.title ?? params.callerAgentId,
    taskBrief,
  });

  scheduleAgentMetadataGeneration({
    agentManager: params.agentManager,
    agentId: snapshot.id,
    cwd: snapshot.cwd,
    initialPrompt: taskBrief,
    explicitTitle: snapshot.config.title,
    appostleHome: params.appostleHome,
    logger: params.logger,
    ownerUserId: parentAgent.ownerUserId,
  });

  params.agentManager.recordUserMessage(snapshot.id, prompt, { emitState: false });
  startAgentRun(params.agentManager, snapshot.id, prompt, params.logger);

  if (params.agentStorage) {
    setupFinishNotification({
      agentManager: params.agentManager,
      agentStorage: params.agentStorage,
      childAgentId: snapshot.id,
      callerAgentId: params.callerAgentId,
      logger: params.logger,
    });
  }

  if (params.onHandoffSpawned) {
    void Promise.resolve(
      params.onHandoffSpawned({
        parentAgentId: params.callerAgentId,
        parentAgentCwd: parentAgent.cwd,
        parentAgentOwnerUserId: parentAgent.ownerUserId,
        childAgentId: snapshot.id,
        childAgentTitle: snapshot.config.title ?? null,
      }),
    ).catch((err) => {
      params.logger.warn(
        { err, childAgentId: snapshot.id, parentAgentId: params.callerAgentId },
        "handoff: onHandoffSpawned callback failed",
      );
    });
  }

  return snapshot;
}
